import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Empty, Rule, ui } from '../ui';
import { formatDate } from '@goodbuild/rules/format';
import { ApiError, useApi, useApiMutation } from '../api';
import { Field, Notice, Screen, Table } from './shared';

interface LiveToken {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  createdAt: string;
}

interface CliDownload {
  version: string;
  url: string;
}

interface MintedToken {
  id: string;
  token: string;
  prefix: string;
  ok: string;
}

interface TokenShownOnce extends MintedToken {
  name: string;
}

const LIVE_TOKENS_KEY = ['admin', 'tokens'] as const;

const PUSH_RECIPE = [
  'gbcli init --product <slug>',
  'export GOODBUILD_TOKEN=gb_...',
  'gbcli build push --version 1.0.0',
  'gbcli build promote <build-id> --to default',
].join('\n');

export function AdminTokensRoute(): React.ReactElement {
  const liveTokens = useApi<{ tokens: LiveToken[]; count: number; cli: CliDownload | null }>(
    LIVE_TOKENS_KEY,
    '/admin/tokens',
  );
  const call = useApiMutation();
  const cache = useQueryClient();

  const [nameForNewToken, setNameForNewToken] = useState('');
  const [tokenShownOnce, setTokenShownOnce] = useState<TokenShownOnce | null>(null);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [awaitingRevokeConfirmation, setAwaitingRevokeConfirmation] = useState<string | null>(null);

  const tokens = liveTokens.data?.tokens ?? [];
  const cli = liveTokens.data?.cli ?? null;
  const named = nameForNewToken.trim();

  async function mint(): Promise<void> {
    setBusy(true);
    setOutcome(null);
    setCopiedToClipboard(false);
    try {
      const issued = await call<MintedToken>('/admin/tokens', {
        method: 'POST',
        body: JSON.stringify({ name: named }),
      });
      setTokenShownOnce({ ...issued, name: named });
      setNameForNewToken('');
      await cache.invalidateQueries({ queryKey: LIVE_TOKENS_KEY });
    } catch (error) {
      setOutcome(error instanceof ApiError ? error.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(tokenId: string): Promise<void> {
    setBusy(true);
    setOutcome(null);
    try {
      const result = await call<{ ok: string }>(`/admin/tokens/${tokenId}`, { method: 'DELETE' });
      setOutcome(result.ok);
      if (tokenShownOnce?.id === tokenId) setTokenShownOnce(null);
      await cache.invalidateQueries({ queryKey: LIVE_TOKENS_KEY });
    } catch (error) {
      setOutcome(error instanceof ApiError ? error.message : 'That did not work. Try again.');
    } finally {
      setAwaitingRevokeConfirmation(null);
      setBusy(false);
    }
  }

  async function copyToClipboard(value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedToClipboard(true);
    } catch {
      setCopiedToClipboard(false);
      setOutcome('Copying did not work. Select the token and copy it by hand.');
    }
  }

  return (
    <Screen
      title="Build tokens"
      aside={tokens.length === 1 ? '1 live' : `${tokens.length} live`}
      query={liveTokens}
    >
      <div className={ui.stack ?? ''}>
        <p className="gb-body">
          A build token is what <code>gbcli</code> uploads a game with. It reads one from{' '}
          <code>GOODBUILD_TOKEN</code> and never asks anybody to sign in, so a build machine can
          push on its own. Any token can push to any game we sell, so give each machine its own —
          that way the right one can be revoked.
        </p>

        {tokenShownOnce ? (
          <div className={ui.panel ?? ''}>
            <div className={ui.stack ?? ''}>
              <p className="gb-eyebrow">{tokenShownOnce.name}</p>
              <code className={ui.numeric ?? ''} style={{ wordBreak: 'break-all' }}>
                {tokenShownOnce.token}
              </code>
              <Notice alert>{tokenShownOnce.ok}</Notice>
              <div className={ui.actions ?? ''}>
                <button
                  type="button"
                  className={ui.button ?? ''}
                  onClick={() => void copyToClipboard(tokenShownOnce.token)}
                >
                  {copiedToClipboard ? 'Copied' : 'Copy it'}
                </button>
                <button
                  type="button"
                  className={ui.buttonSecondary ?? ''}
                  onClick={() => {
                    setTokenShownOnce(null);
                    setCopiedToClipboard(false);
                  }}
                >
                  Hide it
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <form
          className={ui.stack ?? ''}
          onSubmit={(event) => {
            event.preventDefault();
            if (named !== '' && !busy) void mint();
          }}
        >
          <Field label="What this token is for">
            <input
              className={ui.input ?? ''}
              value={nameForNewToken}
              onChange={(event) => setNameForNewToken(event.target.value)}
              placeholder="Release runner, nightly build, the desk machine"
              maxLength={80}
            />
          </Field>

          <div className={ui.actions ?? ''}>
            <button type="submit" className={ui.button ?? ''} disabled={busy || named === ''}>
              Mint a token
            </button>
            <span className={ui.statLabel ?? ''}>
              Shown once, stored as a hash. Nothing here can read it back.
            </span>
          </div>
        </form>

        {outcome ? (
          <p className="gb-body" role="status">
            {outcome}
          </p>
        ) : null}
      </div>

      <Rule />

      {tokens.length === 0 ? (
        <Empty>No live tokens. Nothing can push a build until one is minted.</Empty>
      ) : (
        <Table head={['What it is for', 'Starts with', 'Last used', 'Minted', '']}>
          {tokens.map((token) => (
            <tr key={token.id}>
              <td>{token.name}</td>
              <td className={ui.numeric ?? ''}>{token.prefix}…</td>
              <td className={ui.numeric ?? ''}>
                {token.lastUsedAt ? formatDate(token.lastUsedAt) : 'Never'}
              </td>
              <td className={ui.numeric ?? ''}>{formatDate(token.createdAt)}</td>
              <td>
                {awaitingRevokeConfirmation === token.id ? (
                  <span className={ui.actions ?? ''}>
                    <button
                      type="button"
                      className={ui.button ?? ''}
                      disabled={busy}
                      onClick={() => void revoke(token.id)}
                    >
                      Revoke it
                    </button>
                    <button
                      type="button"
                      className={ui.buttonSecondary ?? ''}
                      disabled={busy}
                      onClick={() => setAwaitingRevokeConfirmation(null)}
                    >
                      Keep it
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className={ui.buttonSecondary ?? ''}
                    disabled={busy}
                    onClick={() => setAwaitingRevokeConfirmation(token.id)}
                  >
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}

      <Rule />

      <h2 className={ui.sectionTitle ?? ''}>Pushing a build with one</h2>

      <div className={ui.stack ?? ''}>
        {cli ? (
          <div className={ui.actions ?? ''}>
            <a className={ui.button ?? ''} href={cli.url}>
              Download gbcli {cli.version}
            </a>
            <span className={ui.statLabel ?? ''}>
              Windows, 64-bit. Signed, so it runs without a warning. Put it somewhere on your PATH.
            </span>
          </div>
        ) : (
          <Notice>
            No build of gbcli has been published yet. The next production deploy builds it, signs it
            and puts the link here.
          </Notice>
        )}

        <pre className={ui.numeric ?? ''} style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
          {PUSH_RECIPE}
        </pre>
      </div>
    </Screen>
  );
}
